import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import type { z } from "zod";
import { CreateRoomSchema } from "@/modules/master/domain/contracts";
import { isDevMode } from "@/shared/lib/config";
import { getRandomMockData, mockRooms } from "../dev-mock-data";

const RoomFormSchema = CreateRoomSchema;
export type RoomFormValues = z.infer<typeof RoomFormSchema>;

export function RoomForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<RoomFormValues>;
  onSubmit: (values: RoomFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<RoomFormValues>({
    resolver: zodResolver(RoomFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      capacity: initialValues?.capacity ?? undefined,
      description: initialValues?.description || "",
      status: initialValues?.status || "active",
    },
  });

  const handleAutoFill = () => {
    const mockData = getRandomMockData(mockRooms);
    methods.reset(mockData);
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormInput name="name" label="ຊື່" placeholder="ປ້ອນຊື່ຫ້ອງຮຽນ" required />

          <FormNumber
            name="capacity"
            label="ຄວາມຈຸ (ຈຳນວນຄົນທີ່ຮັບໄດ້)"
            placeholder="ປ້ອນຄວາມຈຸ (ບໍ່ບັງຄັບ)"
          />
        </div>

        <FormTextarea
          name="description"
          label="ຄຳອະທິບາຍ"
          placeholder="ປ້ອນຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={3}
        />

        <RHF.Controller
          name="status"
          control={methods.control}
          render={({ field }) => (
            <div className="space-y-2">
              <label htmlFor="status" className="font-medium text-sm">
                ສະຖານະ
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={field.value === "active" ? "default" : "outline"}
                  onClick={() => field.onChange("active")}
                >
                  ໃຊ້ງານ
                </Button>
                <Button
                  type="button"
                  variant={field.value === "inactive" ? "default" : "outline"}
                  onClick={() => field.onChange("inactive")}
                >
                  ບໍ່ໃຊ້ງານ
                </Button>
                <Button
                  type="button"
                  variant={
                    field.value === "maintenance" ? "default" : "outline"
                  }
                  onClick={() => field.onChange("maintenance")}
                >
                  ກຳລັງບຳລຸງຮັກສາ
                </Button>
              </div>
            </div>
          )}
        />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
